import React, { useState } from "react";
import {
  Building2,
  MessageCircle,
  ShieldCheck,
  KeyRound,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import Modal from "../common/Modal";
import Input from "../common/Input";

/**
 * Connect or edit a WhatsApp Business account.
 *
 * All the "where do I find this" guidance below is based on Meta's own docs:
 *  - Cloud API get started        https://developers.facebook.com/docs/whatsapp/cloud-api/get-started
 *  - Business Management API       https://developers.facebook.com/docs/whatsapp/business-management-api/get-started
 *  - System user access tokens    https://developers.facebook.com/docs/whatsapp/business-management-api/get-started#system-user-access-tokens
 *  - App settings (ID / secret)    https://developers.facebook.com/docs/development/create-an-app
 *  - Webhook payload validation    https://developers.facebook.com/docs/graph-api/webhooks/getting-started#validate-payloads
 */

const DocLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-0.5 font-medium text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary dark:text-dark-primary"
  >
    {children}
    <ExternalLink size={10} />
  </a>
);

function HelpPanel() {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-theme-100 bg-theme-50/60 dark:border-dark-border dark:bg-dark-soft-blue/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3.5 py-2.5 text-[11px] font-semibold text-primary dark:text-dark-primary"
      >
        Where do I find these values?
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="space-y-3 border-t border-theme-100 px-3.5 py-3 text-[10px] leading-5 text-text-secondary dark:border-dark-border dark:text-dark-text-secondary">
          <div>
            <p className="font-semibold text-text-primary dark:text-dark-text-primary">
              Business ID <span className="font-normal text-text-muted">(Business portfolio)</span>
            </p>
            <p>
              <DocLink href="https://business.facebook.com/settings/info">
                business.facebook.com → Business settings → Business info
              </DocLink>{" "}
              — the number shown as "Business ID".
            </p>
          </div>

          <div>
            <p className="font-semibold text-text-primary dark:text-dark-text-primary">
              WhatsApp Business Account ID <span className="font-normal text-text-muted">(WABA — not the same as Business ID)</span>
            </p>
            <p>
              <DocLink href="https://developers.facebook.com/apps">
                developers.facebook.com/apps
              </DocLink>{" "}
              → your app → <b>WhatsApp → API Setup</b>. It's listed right under
              your phone number. You can also leave this blank and use
              <b> "Detect WhatsApp Business Account"</b> on the Templates page.
            </p>
          </div>

          <div>
            <p className="font-semibold text-text-primary dark:text-dark-text-primary">
              App ID &amp; App Secret
            </p>
            <p>
              <DocLink href="https://developers.facebook.com/apps">
                developers.facebook.com/apps
              </DocLink>{" "}
              → your app → <b>App settings → Basic</b>. The App Secret is used to
              verify inbound webhook signatures (
              <DocLink href="https://developers.facebook.com/docs/graph-api/webhooks/getting-started#validate-payloads">
                X-Hub-Signature-256
              </DocLink>
              ). App ID is optional.
            </p>
          </div>

          <div>
            <p className="font-semibold text-text-primary dark:text-dark-text-primary">
              Access Token <span className="font-normal text-text-muted">(permanent — use a System User)</span>
            </p>
            <p>
              <DocLink href="https://business.facebook.com/settings/system-users">
                business.facebook.com → Business settings → Users → System users
              </DocLink>{" "}
              → add the app → <b>Generate token</b> with{" "}
              <code className="rounded bg-white/70 px-1 dark:bg-black/20">whatsapp_business_messaging</code>,{" "}
              <code className="rounded bg-white/70 px-1 dark:bg-black/20">whatsapp_business_management</code>{" "}
              and{" "}
              <code className="rounded bg-white/70 px-1 dark:bg-black/20">business_management</code>{" "}
              (the last one lets Auto Appy auto-detect the WABA).{" "}
              <DocLink href="https://developers.facebook.com/docs/whatsapp/business-management-api/get-started#system-user-access-tokens">
                Docs
              </DocLink>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppAccountModal({
  form,
  setForm,
  creating,
  onClose,
  onSubmit,
  mode = "create",
  error = "",
}) {
  const isEdit = mode === "edit";
  const set = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));

  return (
    <Modal
      title={isEdit ? "Edit WhatsApp account" : "Connect WhatsApp account"}
      description={
        isEdit
          ? "Update the Meta credentials for this WhatsApp Business account."
          : "Add a WhatsApp Business account to this workspace."
      }
      icon={MessageCircle}
      onClose={onClose}
      onSubmit={onSubmit}
      submitting={creating}
      submitText={isEdit ? "Save changes" : "Connect account"}
    >
      <div className="space-y-5">
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium leading-4 text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-400">
            {error}
          </p>
        )}

        <HelpPanel />

        {/* Business information */}
        <div>
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary dark:text-dark-primary">
            Business information
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Business name"
              name="business_name"
              placeholder="Auto Appy"
              icon={Building2}
              value={form.business_name}
              onChange={set("business_name")}
              disabled={creating}
              hint="Any label — shown in Auto Appy only."
            />

            <Input
              label="Business ID"
              name="business_id"
              placeholder="e.g. 1043210987654321"
              icon={Building2}
              value={form.business_id}
              onChange={set("business_id")}
              disabled={creating}
              hint="Business settings → Business info → 'Business ID'."
            />

            <div className="md:col-span-2">
              <Input
                label="WhatsApp Business Account ID (WABA)"
                name="waba_id"
                placeholder="Leave blank to auto-detect from the token"
                icon={MessageCircle}
                value={form.waba_id}
                onChange={set("waba_id")}
                disabled={creating}
                required={false}
                hint="App dashboard → WhatsApp → API Setup (under the phone number). Not the same as Business ID. Optional — Auto Appy can detect it."
              />
            </div>
          </div>
        </div>

        {/* Meta app credentials */}
        <div>
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary dark:text-dark-primary">
            Meta app credentials
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="App ID"
              name="app_id"
              placeholder="e.g. 3987654321098765"
              icon={ShieldCheck}
              value={form.app_id}
              onChange={set("app_id")}
              disabled={creating}
              required={false}
              hint="App settings → Basic. Optional."
            />

            <Input
              label="App Secret"
              name="app_secret"
              placeholder={isEdit ? "Leave blank to keep current" : "App settings → Basic → App secret"}
              type="password"
              icon={KeyRound}
              value={form.app_secret}
              onChange={set("app_secret")}
              disabled={creating}
              required={!isEdit}
              hint="Used to verify inbound webhook signatures (X-Hub-Signature-256)."
            />

            <div className="md:col-span-2">
              <Input
                label="Access Token"
                name="access_token"
                placeholder={
                  isEdit
                    ? "Leave blank to keep current"
                    : "Permanent System User token"
                }
                type="password"
                icon={KeyRound}
                value={form.access_token}
                onChange={set("access_token")}
                disabled={creating}
                required={!isEdit}
                hint="Business settings → System users → Generate token with whatsapp_business_messaging + whatsapp_business_management + business_management."
              />
            </div>
          </div>
        </div>

        {isEdit && (
          <p className="text-[10px] leading-4 text-text-muted dark:text-dark-text-muted">
            App Secret and Access Token are hidden for security. Leave them blank
            to keep the current values; type a new value only to replace them.
          </p>
        )}
      </div>
    </Modal>
  );
}

export default WhatsAppAccountModal;
